import { useMemo } from "react";
import useSWR from "swr";
import { logger } from "../loggerHelper";

type FetcherKey = string | object | null | false | undefined;
type FetcherFn<TData> = (
  _key: FetcherKey,
  _shouldFetch: boolean
) => Promise<TData>;
type FetcherOptions = Record<string, unknown>;

function useFetcher<TData = unknown>(
  key: FetcherKey,
  fetcherFn?: FetcherFn<TData>,
  options: FetcherOptions = {}
) {
  const serializedKey = key
    ? typeof key === "object"
      ? JSON.stringify(key)
      : key
    : null;

  const fetcher = useMemo(() => {
    if (typeof fetcherFn !== "function") {
      logger.warn("useFetcher: fetcherFn must be a function");
      return () => Promise.reject(new Error("Invalid fetcher function"));
    }
    return () => fetcherFn(key, true);
  }, [serializedKey]);

  const { data, error, isLoading, isValidating, mutate } = useSWR(
    serializedKey,
    fetcher,
    {
      revalidateOnMount: true,
      ...options,
      shouldRetryOnError: false,
      onErrorRetry: () => {},
    }
  );

  return { data, error, isLoading, isValidating, mutate };
}

export default useFetcher;
